import { Controller } from "@hotwired/stimulus";

// Live preview of the real v2 AirRobe widget. Renders the chosen component in a
// sandboxed iframe (loading the shop's source bundle in __previewMode), and
// restyles it as the admin edits the CSS-variable fields. CSS values are sent to
// a server endpoint that reuses the production AbstractConfigPresenter mapping,
// so the preview matches what the widget actually renders.
//
// The widget renders wider than the narrow preview rail, so we lay it out at a
// fixed logical width inside the iframe and scale that down (CSS transform) to fit
// the viewport — the whole widget stays visible with no horizontal scrollbar — and
// size the viewport to the scaled widget height so there's no empty gap below it.
//
// Expected on the wrapping element:
//   data-controller="widget-preview"
//   data-widget-preview-bundle-url-value="<shop.script_source_url>"
//   data-widget-preview-config-url-value="<widget_preview_config path>"
// and, inside, a viewport element (data-widget-preview-target="viewport") wrapping
// an iframe (data-widget-preview-target="frame").
export default class extends Controller {
  static values = { bundleUrl: String, configUrl: String, splitTestVariant: String };
  static targets = ["frame", "viewport"];

  connect() {
    this.component = "opt-in";
    this.lastVars = {};
    this.frameReady = false;
    // Logical width the widget is laid out at inside the iframe before scaling to fit.
    // Comfortably above the widget's 320px min-width so it never overflows internally.
    this.logicalWidth = 380;
    this.onInput = this.debounce(() => this.refresh(), 300);
    this.onResize = this.debounce(() => this.fit(), 150);
    this.element.addEventListener("input", this.onInput);
    this.element.addEventListener("change", this.onInput);
    window.addEventListener("resize", this.onResize);
    this.rebuild();
  }

  disconnect() {
    this.element.removeEventListener("input", this.onInput);
    this.element.removeEventListener("change", this.onInput);
    window.removeEventListener("resize", this.onResize);
    if (this.bodyObserver) this.bodyObserver.disconnect();
  }

  // Switch which widget component is previewed (opt-in / cart / confirmation).
  selectComponent(event) {
    this.component = event.target.value;
    this.rebuild();
  }

  rebuild() {
    if (!this.hasFrameTarget) return;
    this.frameReady = false;
    // Re-apply the current CSS vars and re-fit once the (re)loaded bundle has rendered.
    // applyCss only runs from here (or from a post-ready refresh) so it never targets the
    // transient blank document mid-reload.
    this.frameTarget.onload = () => {
      this.frameReady = true;
      this.applyCss(this.lastVars);
      this.observeContentSize();
      this.fit();
    };
    this.frameTarget.srcdoc = this.buildSrcdoc(this.component);
    this.refresh();
  }

  async refresh() {
    try {
      const url = `${this.configUrlValue}?${this.collectCssParams().toString()}`;
      const res = await fetch(url, { headers: { Accept: "application/json" } });
      if (!res.ok) return; // keep the last good styling on a bad response
      const json = await res.json();
      this.lastVars = json.cssVariables || {};
      // If the frame is still (re)loading, the onload handler will apply lastVars; don't
      // wipe/repaint a document that's about to be replaced.
      if (this.frameReady) this.applyCss(this.lastVars);
    } catch (e) {
      // Non-fatal: keep the last applied styling rather than clearing the preview.
    }
  }

  // Gather the css_* / airrobe_* form values within this controller's element,
  // stripping the `model[...]` wrapper so the endpoint sees flat keys.
  collectCssParams() {
    const params = new URLSearchParams();
    this.element.querySelectorAll("input, select, textarea").forEach((el) => {
      const match = el.name && el.name.match(/\[([^\]]+)\]$/);
      if (!match) return;
      const key = match[1];
      if (!(key.startsWith("css_") || key.startsWith("airrobe_"))) return;
      if ((el.type === "radio" || el.type === "checkbox") && !el.checked) return;
      if (el.value === "") return;
      params.set(key, el.value);
    });
    return params;
  }

  applyCss(vars) {
    const doc = this.hasFrameTarget && this.frameTarget.contentDocument;
    if (!doc || !doc.documentElement) return;
    const root = doc.documentElement;
    // Clear previously-set airrobe vars so cleared fields reset to widget defaults.
    for (let i = root.style.length - 1; i >= 0; i--) {
      const prop = root.style[i];
      if (prop.startsWith("--airrobe-")) root.style.removeProperty(prop);
    }
    Object.entries(vars || {}).forEach(([k, v]) => root.style.setProperty(k, v));
  }

  // The widget renders asynchronously after its bundle loads (and re-renders when the
  // previewed component changes), so re-fit whenever the iframe content changes size.
  observeContentSize() {
    if (this.bodyObserver) this.bodyObserver.disconnect();
    const doc = this.hasFrameTarget && this.frameTarget.contentDocument;
    if (!doc || !doc.body || typeof ResizeObserver === "undefined") return;
    this.bodyObserver = new ResizeObserver(() => this.fit());
    this.bodyObserver.observe(doc.body);
  }

  // Lay the iframe out at logicalWidth and scale it down to fit the viewport, then size
  // the viewport to the scaled widget height so there's no empty space below the widget.
  fit() {
    if (!this.hasViewportTarget || !this.hasFrameTarget) return;
    const available = this.viewportTarget.clientWidth;
    if (!available) return;
    const scale = Math.min(1, available / this.logicalWidth);

    let height = 200;
    const doc = this.frameTarget.contentDocument;
    if (doc && doc.body) {
      const measured = Math.max(doc.body.scrollHeight, doc.documentElement.scrollHeight);
      if (measured > 0) height = Math.min(Math.max(measured, 96), 600);
    }

    // No-op if nothing changed — avoids redundant style writes when the ResizeObserver
    // fires for a content size we've already laid out.
    if (height === this.lastHeight && scale === this.lastScale) return;
    this.lastHeight = height;
    this.lastScale = scale;

    Object.assign(this.frameTarget.style, {
      width: `${this.logicalWidth}px`,
      height: `${height}px`,
      transformOrigin: "top left",
      transform: `scale(${scale})`,
    });
    this.viewportTarget.style.height = `${Math.round(height * scale)}px`;
  }

  buildSrcdoc(component) {
    const samples = {
      "opt-in":
        '<airrobe-opt-in price-cents="12000" rrp-cents="20000" category="Dresses" brand="Demo" currency="AUD" hide-est-resale-value></airrobe-opt-in>',
      cart:
        '<airrobe-multi-opt-in items=\'[{"category":"Dresses"},{"category":"Tops"}]\' currency="AUD"></airrobe-multi-opt-in>',
    };
    const markup = samples[component] || samples["opt-in"];
    // Encode the bundle URL before interpolating it into the srcdoc attribute so a stray
    // quote / angle bracket in shop.script_source_url can't break out of the attribute and
    // inject markup into the (allow-same-origin) iframe.
    const bundleUrl = encodeURI(this.bundleUrlValue || "").replace(/"/g, "%22");
    // Only the widget-variant Appearance preview passes a split-test variant. Setting it makes the
    // v2 widget render the split-test appearance, matching production — where the config presenter
    // emits the variant's `target_split_test_variant` as `window.airrobe.variants[].splitTestVariant`
    // and the widget mixes the chosen variant in. The shop Appearance preview never sets this value,
    // so it keeps showing the base widget. JSON-encode and neutralize "<" so a variant name can't
    // break out of the <script>.
    const airrobe = { __previewMode: true };
    if (this.splitTestVariantValue) {
      airrobe.variants = [{ splitTestVariant: this.splitTestVariantValue, disabled: false }];
    }
    const airrobeJson = JSON.stringify(airrobe).replace(/</g, "\\u003c");
    // The preview iframe has no Shopify storefront context, so we stub the globals the
    // widget reads during setup/render:
    //  - window.Shopify: getDefaultShopifyLocale reads .locale/.country unguarded, so
    //    without it setup() throws "Cannot read properties of undefined (reading 'locale')".
    //  - window.fetch: the widget's network calls are stubbed by URL —
    //      • price-engine.airrobe.com → a sample resale result, so the opt-in widget shows
    //        an estimated resale value (it reads result.resaleValue) instead of failing.
    //      • /cart.json → a sample cart, so the multi opt-in component's
    //        response.items.map(...) doesn't throw "...reading 'map'".
    //      • everything else → a generic eligibility reply.
    // Styling preview only — these stub values don't affect real appearance/behaviour.
    return `<!doctype html><html><head><meta charset="utf-8">
<style>body{margin:0;padding:16px;font-family:-apple-system,system-ui,sans-serif;background:#fff}.wrap{min-width:320px}</style>
<script>
window.airrobe=${airrobeJson};
window.Shopify={locale:"en",country:"AU"};
window.fetch=function(input){
  var u=typeof input==="string"?input:(input&&input.url)||"";
  var b;
  if(u.indexOf("price-engine")>-1){b={engine:"v1.0",result:{hits:{brand:false,category:false,material:false},resaleValuePercentage:55,resaleValue:66,weight:1}};}
  else if(u.indexOf("cart.json")>-1){b={items:[{product_type:"Dresses"},{product_type:"Tops"}]};}
  else{b={data:{isCustomer:false}};}
  return Promise.resolve(new Response(JSON.stringify(b),{status:200,headers:{"Content-Type":"application/json"}}));
};
</script>
</head><body><div class="wrap">${markup}</div>
<script src="${bundleUrl}"></script>
</body></html>`;
  }

  debounce(fn, wait) {
    let timer;
    return (...args) => {
      clearTimeout(timer);
      timer = setTimeout(() => fn.apply(this, args), wait);
    };
  }
}
